import Auth from '../auth/Auth';
import AddLogModel from '../models/AddLogModel';
import ChangePasswordModel from '../models/ChangePasswordModel';
import Customer from '../models/Customer';
import CustomerDetail from '../models/CustomerDetail';
import CustomerLog from '../models/CustomerLog';
import CustomerProduct from "../models/CustomerProduct";
import CustomerRevisions from '../models/CustomerRevisions';
import CustomerStats from '../models/CustomerStats';
import NewCustomer from "../models/NewCustomer";
import SendEmailModel from '../models/SendEmailModel';
import { WebClient } from './WebClient'

class ApiCustomer extends WebClient {
    async getCustomers(includeDeactivated: boolean): Promise<Customer[] | null> {
        let url = includeDeactivated ? "/customer/incd" : "/customer";

        return this.get(url, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return null;
        }).catch(() => { return null; });
    }

    async getCustomersByFleetSeatBase(fleet: string, seat: string, base: string): Promise<Customer[] | null> {
        let url = `/customer/${fleet || "any"}/${seat || "any"}/${base || "any"}`;

        return this.get(url, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return null;
        }).catch(() => { return null; });
    }

    async getCustomer(customerId: number): Promise<CustomerDetail | null> {
        return this.get(`/customer/${customerId}`, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return null;
        }).catch(() => { return null; });
    }

    async getCustomerRevisions(customerId: number): Promise<CustomerRevisions | null> {
        return this.get(`/customer/${customerId}/revisions`, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return null;
        }).catch(() => { return null; });
    }

    async getCustomerStats(customerId: number): Promise<CustomerStats | null> {
        return this.get(`/stats/${customerId}`, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return null;
        }).catch(() => { return null; });
    }

    async updateCustomerStats(customerId: number, fleet: string, seat: string, base: string): Promise<boolean> {
        const data = {
            customerId,
            aircraftGroup: fleet,
            position: seat,
            base
        };
        return this.put("/stats", data, Auth.getAuthToken()).then(res => {
            return res.status === 200;
        }).catch((e) => {
            return false;
        });
    }

    async updatePassword(pwds: ChangePasswordModel): Promise<boolean> {
        return await this.put(`/customer/${pwds.id}/pwd`, pwds, Auth.getAuthToken()).then(res => {
            return res.status === 200;
        })
            .catch(() => { return false; });
    }

    async resetPassword(customerId: number): Promise<boolean> {
        return await this.get(`/customer/${customerId}/resetpwd`, Auth.getAuthToken()).then(res => {
            return res.status === 200;
        })
            .catch(() => { return false; });
    }

    async getLogs(customerId: number, topCount: number, includeBatch: boolean): Promise<CustomerLog[] | null> {
        if (topCount < 1) {
            return this.get(`/log/${customerId}/${includeBatch}`, Auth.getAuthToken()).then(res => {
                if (res.status === 200) {
                    return res.data;
                }
                return null;
            }).catch(() => { return null; });
        } else {
            return this.get(`/log/${customerId}/top/${topCount}/${includeBatch}`, Auth.getAuthToken()).then(res => {
                if (res.status === 200) {
                    return res.data;
                }
                return null;
            }).catch(() => { return null; });
        }
    }

    async addLog(data: AddLogModel): Promise<CustomerLog> {
        return this.post(`/log`, data, Auth.getAuthToken()).then(res => {
            if (res.data) {
                return res.data;
            }
        }).catch(() => { return; });
    }

    async updateLog(data: CustomerLog): Promise<CustomerLog> {
        return this.put(`/log`, data, Auth.getAuthToken()).then(res => {
            if (res.data) {
                return res.data;
            }
        }).catch(() => { return; });
    }

    async sendEmail(data: SendEmailModel): Promise<boolean> {
        return this.post(`/email/send`, data, Auth.getAuthToken()).then(res => {
            return res.status === 200;
        }).catch(() => { return false; });
    }

    async skipCustomer(customerId: number): Promise<boolean> {
        return this.get(`/customer/${customerId}/skip/true`, Auth.getAuthToken()).then(res => {
            return res.status === 200;
        }).catch(() => { return false; });
    }

    async unSkipCustomer(customerId: number): Promise<boolean> {
        return this.get(`/customer/${customerId}/skip/false`, Auth.getAuthToken()).then(res => {
            return res.status === 200;
        }).catch(() => { return false; });
    }

    async deactivateCustomer(customerId: number, reason: string): Promise<boolean> {
        var data = { "reason": reason };
        return this.put(`/customer/${customerId}/deactivate`, data, Auth.getAuthToken()).then(res => {
            return res.status === 200;
        }).catch(() => { return false; });
    }

    async activateCustomer(customerId: number, clearProfiles: boolean): Promise<boolean> {
        return this.get(`/customer/${customerId}/reactivate/${clearProfiles}`, Auth.getAuthToken()).then(res => {
            return res.status === 200;
        }).catch(() => { return false; });
    }

    async validateUserId(userId: string): Promise<boolean> {
        return this.get(`/customer/checkuserid/${userId}`, Auth.getAuthToken()).then(res => {
            return (res.status === 200);
        }).catch(() => { return false; });
    }

    async createNewCustomer(customer: NewCustomer): Promise<CustomerDetail | null> {
        return this.post(`/customer`, customer, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return null;
        }).catch((e) => { return null; });
    }

    async updateCustomer(customer: CustomerDetail): Promise<CustomerDetail | null> {
        return this.put(`/customer`, customer, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return null;
        }).catch((e) => { return null; });
    }

    async deleteCustomer(customerId: number): Promise<boolean> {
        return this.delete(`/customer/${customerId}`, Auth.getAuthToken()).then(res => {
            return (res.status === 200);
        }).catch((e) => { return false; });
    }

    async getCustomerProducts(customerId: number): Promise<CustomerProduct[]> {
        return this.get(`/product/${customerId}`, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return [];
        }).catch(() => { return []; });
    }

    async updateCustomerProduct(customerProd: CustomerProduct): Promise<CustomerProduct> {
        return this.put(`/product`, customerProd, Auth.getAuthToken()).then(res => {
            if (res.status === 200) {
                return res.data;
            }
            return customerProd;
        }).catch(() => { return customerProd; });
    }

    async changeProductSkip(customerId: number, productId: number, skip: boolean): Promise<boolean> {
        return this.get(`/product/${customerId}/${productId}/${skip}`, Auth.getAuthToken()).then(res => {
            return (res.status === 200);
        }).catch(() => { return false; });
    }
}

export default new ApiCustomer();
